/**
 * PayoutHistoryTable (Plan 20).
 *
 * Paginated history rows below the next-payout card. Each row is
 * clickable; the parent opens a detail drawer on click. The Plan-20
 * mockup intentionally mirrors the Plan-14 InstitutionalInvoiceTable
 * shape (Status chip + date + amount + chevron) so the visual rhythm
 * across `/billing/school` and `/billing/kitchen` is consistent.
 *
 * Edge cases handled:
 *   - All-CLAWBACK envelope → total renders with destructive tone.
 *   - HELD envelope → status chip embeds a deep-link to `/tasks`; the
 *     chip's own click is stopped so the row click still opens the
 *     drawer.
 *   - Empty list → friendly empty-state copy in a single-column row.
 */

import { Table, TableBody, TableCell, TableContainer, TableHead, TableHeader, TableRow } from '@ordo/ui/table'
import { ChevronRight } from 'lucide-react'

import type { KitchenPayoutRow } from '@/api/billing-kitchen'
import { cn } from '@/lib/utils'

import { formatCents, formatCycleDateShort, formatPayoutPeriod, isNegativePayout } from './kitchen-payouts-helpers'
import { PayoutStatusChip } from './payout-status-chip'

export interface PayoutHistoryTableProps {
  rows: readonly KitchenPayoutRow[]
  onSelect: (row: KitchenPayoutRow) => void
}

export function PayoutHistoryTable({ rows, onSelect }: PayoutHistoryTableProps) {
  if (rows.length === 0) {
    return <EmptyHistoryState />
  }
  return (
    <TableContainer>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Cycle date</TableHead>
            <TableHead>Items</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Total</TableHead>
            <TableHead className="w-[36px]" aria-label="Open detail" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <HistoryRow key={row.id} row={row} onSelect={() => onSelect(row)} />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

function HistoryRow({ row, onSelect }: { row: KitchenPayoutRow; onSelect: () => void }) {
  const negative = isNegativePayout(row)
  const period = formatPayoutPeriod(row.periodStart, row.periodEnd)
  return (
    <TableRow
      className="cursor-pointer hover:bg-muted/40"
      onClick={(e) => {
        // PayoutStatusChip can wrap a Link (HELD → /tasks). Don't
        // hijack that interaction with the row click.
        const target = e.target as HTMLElement
        if (target.closest('[data-row-stop="true"]')) {
          return
        }
        onSelect()
      }}>
      <TableCell className="whitespace-nowrap text-sm">
        <span className="block">{formatCycleDateShort(row.cycleDate)}</span>
        {/* The settled DELIVERY window — the cycle date is only its upper bound. */}
        {period ? <span className="block text-xs text-muted-foreground">{period}</span> : null}
      </TableCell>
      <TableCell className="whitespace-nowrap text-sm tabular-nums text-muted-foreground">
        {row.lineCount.toLocaleString()}
      </TableCell>
      <TableCell>
        <span data-row-stop="true">
          <PayoutStatusChip
            status={row.status}
            title={row.failureReason ?? row.heldReason ?? null}
            linkHeldToTasks={false}
          />
        </span>
      </TableCell>
      <TableCell className={cn('text-right font-medium tabular-nums', negative && 'text-destructive')}>
        {formatCents(row.totalCents, row.currency)}
      </TableCell>
      <TableCell className="w-[36px] text-muted-foreground">
        <ChevronRight className="h-4 w-4" aria-hidden />
      </TableCell>
    </TableRow>
  )
}

function EmptyHistoryState() {
  return (
    <div className="flex flex-col items-center gap-2 rounded-md border border-dashed border-border bg-muted/10 px-4 py-12 text-center text-sm">
      <p className="font-medium text-foreground">No past payouts</p>
      <p className="max-w-md text-xs text-muted-foreground">
        Past payouts will appear here once your kitchen ships its first cycle. Tipalti payment status will reconcile
        automatically.
      </p>
    </div>
  )
}
